import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaSave, 
  FaTimes, 
  FaSpinner,
  FaGithub,
  FaExternalLinkAlt,
  FaImage
} from 'react-icons/fa';
import { projectsAPI, uploadAPI } from '../../utils/api.js';
import VisibilityToggle from './VisibilityToggle';
import { usePortfolio } from '../../context/PortfolioContext';

// Helper to extract image id from image_url or upload response
function extractImageIdFromUrl(url) {
  // If your image URLs are like: https://.../images/12345-filename.jpg, you may need to map this to the DB id
  // If you store the image id in the upload response, prefer that
  // For now, assume the backend returns response.data.id
  return null; // fallback if not available
}

const ProjectsEditor = () => {
  const { portfolioData, addProject, updateProject, deleteProject, loading } = usePortfolio();
  const projects = portfolioData?.projects || [];
  // Add categories state
  const [categories, setCategories] = useState(() => {
    // Try to extract unique categories from existing projects
    const cats = new Set();
    (projects || []).forEach(p => {
      if (Array.isArray(p.categories)) p.categories.forEach(cat => cats.add(cat));
      else if (p.category) cats.add(p.category);
    });
    return Array.from(cats);
  });
  const [newCategory, setNewCategory] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [editingProject, setEditingProject] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  // Add category to newProject state
  const [newProject, setNewProject] = useState({
    title: '',
    description: '',
    tech_stack: '',
    github_url: '',
    live_url: '',
    image_url: '',
    visible: true,
    categories: []
  });
  const [toggleLoading, setToggleLoading] = useState({});

  // Remove local projects state and use context directly
  // Remove useEffect(() => { loadProjects(); }, []);
  // Remove loadProjects function

  const handleAddProject = async () => {
    setIsSaving(true);
    setMessage('');
    try {
      const result = await addProject(newProject);
      if (result.success) {
        setNewProject({
          title: '',
          description: '',
          tech_stack: '',
          github_url: '',
          live_url: '',
          image_url: '',
          visible: true,
          categories: []
        });
        setShowAddForm(false);
        setMessage('Project added successfully!');
      } else {
        setMessage(result.error || 'Error adding project');
      }
    } catch (error) {
      setMessage('Error adding project');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateProject = async () => {
    setIsSaving(true);
    setMessage('');
    try {
      const result = await updateProject(editingProject.id, editingProject);
      if (result.success) {
        setEditingProject(null);
        setMessage('Project updated successfully!');
      } else {
        setMessage(result.error || 'Error updating project');
      }
    } catch (error) {
      setMessage('Error updating project');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      const result = await deleteProject(id);
      if (result.success) {
        setMessage('Project deleted successfully!');
      } else {
        setMessage(result.error || 'Error deleting project');
      }
    } catch (error) {
      setMessage('Error deleting project');
    }
  };

  const handleImageUpload = async (file, projectId = null) => {
    try {
      // If editing, delete the old image first
      if (projectId && editingProject && editingProject.image_url) {
        // Try to get the image id from the previous upload (if you store it)
        // If not, you may need to add logic to store image id in the project
        // For now, try to delete by file_path or skip if not available
        // Optionally, you can call uploadAPI.deleteImage(imageId) here
      }
      const response = await uploadAPI.uploadImage(file);
      if (response.success) {
        const imageUrl = response.data.url;
        // If you get image id from response, store it for future deletes
        const imageId = response.data.id;
        if (projectId && editingProject) {
          const updatedProject = { ...editingProject, image_url: imageUrl, image_id: imageId };
          setEditingProject(updatedProject);
          await updateProject(editingProject.id, updatedProject);
        } else {
          setNewProject({ ...newProject, image_url: imageUrl, image_id: imageId });
        }
        setMessage('Image uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setMessage('Error uploading image');
    }
  };

  const handleToggleVisibility = async (index, checked) => {
    const project = projects[index];
    setToggleLoading(prev => ({ ...prev, [project.id]: true }));
    try {
      const updatedProject = { ...project, visible: checked };
      const response = await projectsAPI.update(project.id, updatedProject);
      if (response.success) {
        // setProjects(projects.map((p, i) => i === index ? response.data : p)); // This line was removed as per the new_code
      }
    } catch (error) {
      // Optionally show error
    } finally {
      setToggleLoading(prev => ({ ...prev, [project.id]: false }));
    }
  };

  // Add this function for toggling visibility in the projects list
  const handleProjectChange = async (index, field, value) => {
    const updatedProject = { ...projects[index], [field]: value };
    await updateProject(updatedProject.id, updatedProject);
    // No need to update local state, context will refresh and re-render
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FaSpinner className="animate-spin text-4xl text-primary" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800 rounded-lg p-6"
    >
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-white">Projects Manager</h3>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
        >
          <FaPlus />
          Add Project
        </motion.button>
      </div>

      {message && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`mb-4 p-3 rounded-lg ${
            message.includes('Error') 
              ? 'bg-red-500/20 text-red-300 border border-red-500/30' 
              : 'bg-green-500/20 text-green-300 border border-green-500/30'
          }`}
        >
          {message}
        </motion.div>
      )}

      {/* Add Project Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6 p-4 bg-gray-700 rounded-lg"
          >
            <h4 className="text-lg font-medium text-white mb-4">Add New Project</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Project Title *
                </label>
                <input
                  type="text"
                  value={newProject.title}
                  onChange={(e) => setNewProject({ ...newProject, title: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                  placeholder="Project Name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Tech Stack
                </label>
                <input
                  type="text"
                  value={newProject.tech_stack}
                  onChange={(e) => setNewProject({ ...newProject, tech_stack: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                  placeholder="React, Node.js, MongoDB"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description *
                </label>
                <textarea
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary resize-vertical"
                  placeholder="Project description..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  GitHub URL
                </label>
                <input
                  type="url"
                  value={newProject.github_url}
                  onChange={(e) => setNewProject({ ...newProject, github_url: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                  placeholder="https://github.com/username/project"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Live URL
                </label>
                <input
                  type="url"
                  value={newProject.live_url}
                  onChange={(e) => setNewProject({ ...newProject, live_url: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                  placeholder="https://project-demo.com"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Project Image
                </label>
                <div className="flex gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleImageUpload(file);
                    }}
                    className="flex-1 px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                  />
                  {newProject.image_url && (
                    <div className="flex items-center gap-2 mt-2">
                      <img src={newProject.image_url} alt="Preview" className="w-16 h-16 object-cover rounded" />
                      <button
                        type="button"
                        className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                        onClick={() => setNewProject({ ...newProject, image_url: '' })}
                      >
                        Remove Image
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Categories
                </label>
                <select
                  multiple
                  value={newProject.categories}
                  onChange={e => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                    setNewProject({ ...newProject, categories: selected });
                  }}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                  <option value="__add_new__">Add New Category</option>
                </select>
                {newProject.categories.includes('__add_new__') && (
                  <div className="flex gap-2 mt-2">
                    <input
                      type="text"
                      value={newCategory}
                      onChange={e => setNewCategory(e.target.value)}
                      className="flex-1 px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                      placeholder="Type new category"
                    />
                    <button
                      type="button"
                      className="px-4 py-2 bg-primary text-white rounded-lg"
                      onClick={() => {
                        if (newCategory && !categories.includes(newCategory)) {
                          setCategories([...categories, newCategory]);
                          setNewProject({
                            ...newProject,
                            categories: newProject.categories.filter(c => c !== '__add_new__').concat(newCategory)
                          });
                          setNewCategory('');
                        }
                      }}
                    >
                      Add
                    </button>
                  </div>
                )}
              </div>

              <VisibilityToggle
                  checked={newProject.visible}
                  onChange={e => setNewProject({ ...newProject, visible: e.target.checked })}
                loading={false}
                />
            </div>
            
            <div className="flex gap-3 mt-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleAddProject}
                disabled={isSaving || !newProject.title || !newProject.description}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {isSaving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                {isSaving ? 'Adding...' : 'Add Project'}
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setShowAddForm(false);
                  setNewProject({
                    title: '',
                    description: '',
                    tech_stack: '',
                    github_url: '',
                    live_url: '',
                    image_url: '',
                    visible: true,
                    categories: []
                  });
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <FaTimes />
                Cancel
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Projects List */}
      <div className="space-y-4">
        {projects.map((project, index) => (
          <motion.div
            key={project.id}
            layout
            className="bg-gray-700 rounded-lg p-4"
          >
            {editingProject?.id === project.id ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={editingProject.title}
                    onChange={(e) => setEditingProject({ ...editingProject, title: e.target.value })}
                    className="px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white focus:outline-none focus:border-primary"
                    placeholder="Project Title"
                  />
                  <input
                    type="text"
                    value={editingProject.tech_stack}
                    onChange={(e) => setEditingProject({ ...editingProject, tech_stack: e.target.value })}
                    className="px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white focus:outline-none focus:border-primary"
                    placeholder="Tech Stack"
                  />
                  <textarea
                    value={editingProject.description}
                    onChange={(e) => setEditingProject({ ...editingProject, description: e.target.value })}
                    rows={3}
                    className="md:col-span-2 px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white focus:outline-none focus:border-primary resize-vertical"
                    placeholder="Description"
                  />
                  <input
                    type="url"
                    value={editingProject.github_url}
                    onChange={(e) => setEditingProject({ ...editingProject, github_url: e.target.value })}
                    className="px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white focus:outline-none focus:border-primary"
                    placeholder="GitHub URL"
                  />
                  <input
                    type="url"
                    value={editingProject.live_url}
                    onChange={(e) => setEditingProject({ ...editingProject, live_url: e.target.value })}
                    className="px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white focus:outline-none focus:border-primary"
                    placeholder="Live URL"
                  />
                </div>
                
                <div className="flex gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleImageUpload(file, project.id);
                    }}
                    className="flex-1 px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white focus:outline-none focus:border-primary"
                  />
                  {editingProject.image_url && (
                    <div className="flex items-center gap-2 mt-2">
                      <img src={editingProject.image_url} alt="Preview" className="w-16 h-16 object-cover rounded" />
                      <button
                        type="button"
                        className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                        onClick={async () => {
                          // Delete the old image from Supabase if image_id is available
                          if (editingProject.image_id) {
                            try { await uploadAPI.deleteImage(editingProject.image_id); } catch (e) { /* ignore */ }
                          }
                          const updatedProject = { ...editingProject, image_url: '', image_id: '' };
                          setEditingProject(updatedProject);
                          await updateProject(editingProject.id, updatedProject);
                        }}
                      >
                        Remove Image
                      </button>
                    </div>
                  )}
                </div>

                <VisibilityToggle
                    checked={editingProject.visible}
                    onChange={e => setEditingProject({ ...editingProject, visible: e.target.checked })}
                  loading={false}
                  />
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Categories
                  </label>
                  <select
                    multiple
                    value={editingProject.categories || []}
                    onChange={e => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setEditingProject({ ...editingProject, categories: selected });
                    }}
                    className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                    <option value="__add_new__">Add New Category</option>
                  </select>
                  {editingProject.categories && editingProject.categories.includes('__add_new__') && (
                    <div className="flex gap-2 mt-2">
                      <input
                        type="text"
                        value={newCategory}
                        onChange={e => setNewCategory(e.target.value)}
                        className="flex-1 px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                        placeholder="Type new category"
                      />
                      <button
                        type="button"
                        className="px-4 py-2 bg-primary text-white rounded-lg"
                        onClick={() => {
                          if (newCategory && !categories.includes(newCategory)) {
                            setCategories([...categories, newCategory]);
                            setEditingProject({
                              ...editingProject,
                              categories: (editingProject.categories || []).filter(c => c !== '__add_new__').concat(newCategory)
                            });
                            setNewCategory('');
                          }
                        }}
                      >
                        Add
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={handleUpdateProject}
                    disabled={isSaving}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {isSaving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setEditingProject(null)}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                  >
                    <FaTimes />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-4">
                {project.image_url && (
                  <img 
                    src={project.image_url} 
                    alt={project.title} 
                    className="w-24 h-24 object-cover rounded"
                  />
                )}
                
                <div className="flex-1">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-lg font-medium text-white">{project.title}</h4>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingProject(project)}
                        className="p-1 text-blue-400 hover:text-blue-300"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDeleteProject(project.id)}
                        className="p-1 text-red-400 hover:text-red-300"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                  
                  <p className="text-gray-300 mb-2">{project.description}</p>
                  
                  {project.tech_stack && (
                    <p className="text-sm text-gray-400 mb-2">
                      <strong>Tech:</strong> {project.tech_stack}
                    </p>
                  )}
                  
                  <div className="flex gap-3">
                    {project.github_url && (
                      <a
                        href={project.github_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm"
                      >
                        <FaGithub />
                        GitHub
                      </a>
                    )}
                    {project.live_url && (
                      <a
                        href={project.live_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-green-400 hover:text-green-300 text-sm"
                      >
                        <FaExternalLinkAlt />
                        Live Demo
                      </a>
                    )}
                  </div>
                </div>
                <VisibilityToggle
                  checked={project.visible ?? true}
                  onChange={e => handleProjectChange(index, 'visible', e.target.checked)}
                  loading={!!toggleLoading[project.id]}
                />
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default ProjectsEditor; 